<script setup lang="ts">
import { ref } from "vue";
import { defineAsyncComponent } from "vue";

const InputNumber = defineAsyncComponent(() => import("primevue/inputnumber"));

// Props
const props = defineProps<{
  title: string;
  description: string;
  badgeText?: string;
  offerText?: string;
  countLabel?: string;
  defaultCount?: number;
  icon: string;
  buttonLabel?: string;
  showCounter?: boolean;
  page?: { name: string };
  index?: number;
}>();

const emit = defineEmits(["openModal"]);
const count = ref(props.defaultCount || 1);
const content = ref("");
const hourError = ref("");
const openModal = () => {
  if (props.title?.includes("Full Time Dedicated")) {
    content.value = `Hire dedicated ${count.value} ${props.page?.name} Developer(s)`;
  } else if (props.title?.includes("Part-Time")) {
    content.value = `Hire part time ${count.value} ${props.page?.name} Developer(s)`;
  } else if (props.title?.includes("Hourly Basis")) {
    if (!validateForm()) return;
    content.value = `I want to hire WebReinvent for ${
      count.value >= 100
        ? count.value + Math.floor(count.value / 100) * 5
        : count.value
    } Hours`;
  } else if (props.title?.includes("Project Basis")) {
    content.value = `I want to discuss about ${props.page?.name} project`;
  } else {
    emit("openModal");
    return;
  }
  emit("openModal", content.value);
};

const validateForm = () => {
  const validations = [validHour()];

  return validations.every((valid) => valid);
};

const validHour = () => {
  if (count.value < 100) {
    hourError.value = `Please Enter Value greater than 100`;
    return false;
  }
  if (count.value % 50 !== 0) {
    const lower = Math.floor(count.value / 50) * 50;
    const upper = Math.ceil(count.value / 50) * 50;
    hourError.value = `Please enter a valid value. The two nearest valid values are ${lower} and ${upper}`;
    return false;
  } else {
    hourError.value = "";
    return true;
  }
};

watch(
  count,
  (newValue) => {
    if (newValue) {
      console.log("value changed", newValue);
      validHour();
    }
  },
  {
    immediate: true,
  }
);

const onPasteHandler = (event: ClipboardEvent) => {
  event.preventDefault();
  return false;
};
</script>

<template>
  <div
    class="flex flex-col max-w-sm p-6 bg-white dark:bg-transparent rounded-2xl dark:[box-shadow:0_0_.15em_.1em_rgba(98,69,116,0.5)] dark:hover:[box-shadow:0_0_.15em_.1em_rgba(98,69,116,0.7)] transition-all duration-700 hover:[box-shadow:0_.0625rem_.75rem_0_rgba(38,57,82,.2)] [box-shadow:0_.3em_.3em_-.3em_hsla(0,0%,4%,.1),_0_0_0_.1rem_hsla(0,0%,4%,.08)]"
  >
    <!-- Icon & Badge -->
    <div class="flex items-center justify-between mb-4">
      <NuxtImg
        :src="icon"
        alt="Icon"
        width="50"
        height="50"
        loading="lazy"
        format="webp"
      />
      <span
        v-if="badgeText"
        class="text-xs px-3 py-1 bg-[#e3effe] text-[#2e5aa8] rounded-full"
      >
        {{ badgeText }}
      </span>
    </div>

    <!-- Title -->
    <h3 class="text-xl font-extrabold mb-3">{{ title }}</h3>

    <!-- Description -->
    <p class="text-sm mb-4 leading-relaxed">
      {{ description }}
    </p>

    <!-- Offer -->
    <div
      v-if="offerText"
      class="text-xs text-green-700 bg-green-100 p-4 rounded-md mb-4 border-l-4 border-green-700"
      v-html="
        `Get <strong>${
          Math.floor(count / 100) * 5
        }</strong> free on purchase of <strong>${count}</strong> hours`
      "
    />

    <!-- Counter and CTA -->
    <div
      class="flex justify-between items-end mt-auto"
      :class="showCounter ? 'items-end' : 'justify-end'"
    >
      <!-- Input Counter -->
      <div
        v-show="showCounter"
        class="flex flex-col"
        :aria-hidden="!showCounter"
      >
        <label
          v-if="countLabel"
          class="text-xs text-gray-500 mb-1 pl-1"
          for="dev-count"
        >
          {{ countLabel }}
        </label>
        <InputNumber
          v-if="index === 0"
          v-model="count"
          inputId="dev-count"
          autocomplete="current-number"
          showButtons
          buttonLayout="horizontal"
          placeholder="100"
          :step="1"
          decrementButtonClass="p-button-outlined p-button-secondary"
          incrementButtonClass="p-button-outlined p-button-secondary"
          class="*:max-w-20"
          @paste="onPasteHandler"
          @input="validHour"
          @blur="validHour"
        />

        <InputNumber
          v-else
          v-model="count"
          inputId="dev-count"
          autocomplete="current-number"
          placeholder="1"
          showButtons
          buttonLayout="horizontal"
          :min="1"
          :step="1"
          decrementButtonClass="p-button-outlined p-button-secondary"
          incrementButtonClass="p-button-outlined p-button-secondary"
          class="*:max-w-20"
          @paste="onPasteHandler"
        />
      </div>

      <!-- CTA -->
      <WrButton @click="openModal" append-class="flex items-center gap-2"
        >{{ buttonLabel || "Hire" }}
        <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M17 8l4 4m0 0l-4 4m4-4H3"
          />
        </svg>
      </WrButton>
    </div>
    <small v-if="hourError && index == 0" class="text-red-500 mt-1">{{
      hourError
    }}</small>
  </div>
</template>
